import {enableProdMode, ErrorHandler, importProvidersFrom, Injectable, LOCALE_ID} from '@angular/core';

import {environment} from './environments/environment';

import { AllEnterpriseModule, LicenseManager, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-enterprise';
import {AppComponent} from './app/app.component';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {CoreModule} from './app/@core/core.module';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {I18nService} from './app/@core/utils/i18n.service';
import {APP_BASE_HREF} from '@angular/common';
import * as Sentry from '@sentry/browser';
import pkg from '../package.json';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {
  NbCalendarYearModelService,
  NbDatepickerModule,
  NbDateService,
  NbDialogModule,
  NbGlobalLogicalPosition,
  NbMenuModule,
  NbSidebarModule,
  NbThemeModule,
  NbToastrModule,
  NbWindowModule
} from '@nebular/theme';
import {KPI_THEME} from './app/@theme/styles/theme.kpi';

import * as Highcharts from 'highcharts';
import HighchartsCustomEvents from 'highcharts-custom-events';
import HighchartsGroupedCategories from 'highcharts-grouped-categories';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsSolidGauge from 'highcharts/modules/solid-gauge';
import HighchartsTimeline from 'highcharts/modules/timeline';
import HighchartsBrokenAxis from 'highcharts/modules/broken-axis';
import HighchhartsAccessibility from 'highcharts/modules/accessibility';
import {ExtraOptions, RouterModule} from '@angular/router';
import {NOHASHED_ROUTES, ROUTES} from './app/routes';
import {AG_GRID_LICENSE_KEY} from './license-keys';
import {hmrBootstrap} from './hmr';


// Enable needed Highcharts extensions
HighchartsGroupedCategories(Highcharts);
HighchartsMore(Highcharts);
HighchartsCustomEvents(Highcharts);
HighchartsExporting(Highcharts);
HighchartsHeatmap(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsSolidGauge(Highcharts);
HighchartsTimeline(Highcharts);
HighchartsBrokenAxis(Highcharts);
HighchhartsAccessibility(Highcharts);

const CACHE_TOKEN = `?cacheBuster=${pkg?.version}`;

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(
        http,
        '/assets/i18n/',
        '.json' + CACHE_TOKEN,
    );
}

Sentry.init({
    dsn: environment.sentryDSN,
    environment: environment.name,
    release: 'dashboard@' + pkg.version,
    ignoreErrors: [
        'Non-Error exception captured',
    ],
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
    constructor() { }
    handleError(error) {
        Sentry.captureException(error.originalError || error);
        throw error;
    }
}

@Injectable()
export class LegacyCalendarYearModelService<D> extends NbCalendarYearModelService<D> {
    constructor(dateService: NbDateService<D>) {
        super(dateService);
        this.yearsInView = 24;
        this.yearsInRow = 4;
    }

    getViewYears(viewYear: D): D[][] {
        const viewStartYear = this.dateService.getYear(viewYear);
        const years = Array.from({ length: this.yearsInView }, (_, index) =>
            this.copyWithYear(viewStartYear + index, viewYear)
        );

        return Array.from({ length: Math.ceil(years.length / this.yearsInRow) }, (_, rowIndex) =>
            years.slice(rowIndex * this.yearsInRow, (rowIndex + 1) * this.yearsInRow)
        );
    }
}

const NB_THEME_PROVIDERS = [
    ...NbThemeModule.forRoot(
        {
            name: KPI_THEME.name,
        },
        [KPI_THEME],
    ).providers,
    ...NbSidebarModule.forRoot().providers,
    ...NbMenuModule.forRoot().providers,
    ...NbDialogModule.forRoot().providers,
    ...NbWindowModule.forRoot().providers,
    ...NbToastrModule.forRoot({
        position: NbGlobalLogicalPosition.BOTTOM_END,
        duration: 1500,
        status: 'basic',
        hasIcon: true,
        icon: 'checkmark-circle-2-outline',
    }).providers,
];

const config: ExtraOptions = {
    useHash: true
};

const bootstrap = () => bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            NbDialogModule,
            NbDatepickerModule.forRoot(),
            BrowserModule,
            RouterModule.forRoot(NOHASHED_ROUTES, ),
            RouterModule.forRoot(ROUTES, config),
            FontAwesomeModule,
            NgbModule,
            CoreModule.forRoot(),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient],
                },
            }),
        ),
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: ErrorHandler, useClass: SentryErrorHandler },
        {
            provide: LOCALE_ID,
            deps: [I18nService],
            useFactory: (i18nService) => i18nService.getCulture(),
        },
        { provide: NbCalendarYearModelService, useClass: LegacyCalendarYearModelService },
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        ...NB_THEME_PROVIDERS,
    ],

});

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY)
// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: "legacy" as any });

if (environment.production) {
    enableProdMode();
    bootstrap();
} else {
    if (module['hot']) {
        hmrBootstrap(module, bootstrap);
    } else {
        bootstrap();
    }
}
